import { InPageAlert, TextLink } from '@snsw-gel/react';
import React from 'react';
import config from '../../config/config';
import { TollAccountFailure } from '../../services/api-services';
import { PhoneElement } from '../../utils/elements';
import routes from '../../routes';

interface ApiErrorAlertProps {
    readonly errorReason: TollAccountFailure;
}

// TODO remove this file after Tr2 decom
const ApiErrorAlert: React.FC<ApiErrorAlertProps> = ({ errorReason }) => {
    const [title, description] = errorTitleAndDescription(errorReason);

    return <InPageAlert variant='error' title={title} role='alert'>
        {description}
    </InPageAlert>;
};

function errorTitleAndDescription(errorReason: TollAccountFailure): [string, React.JSX.Element] {
    let title: string;
    let description: React.JSX.Element;
    switch (errorReason) {
        case TollAccountFailure.tollAccountAssociatedToABN:
            title = 'Unable to link this toll account';
            description = <>
                <p>This is a business toll account and not a personal or mixed personal toll account.</p>
                <p>Business toll accounts can only be linked as a 'business toll account'.</p>
                <TextLink href={routes.manageTollAccount.path}>
                    Manage toll accounts
                </TextLink>
            </>;
            break;
        case TollAccountFailure.myAccountOutsideNSW:
            title = 'Unable to link account outside of NSW';
            description = <>
                <p>Your MyServiceNSW account address details indicates you are not living in NSW. The Toll Relief scheme
                    is available for{' '}
                    <TextLink isExternal href={config.links.personalEligibilityLink}
                    >eligible customers
                    </TextLink>
                    {' '}
                    living in NSW.
                </p>
                <p>If you think you’re eligible, please call us on {PhoneElement}.</p>
            </>;
            break;
        case TollAccountFailure.tollAccountInactive:
            title = 'Unable to link toll account';
            description = <>
                <p>Your toll account has a status of suspended or inactive.</p>
                <p> To resolve this, contact your toll provider.</p>
            </>;
            break;
        case TollAccountFailure.tomeeTollAccountNotFound:
            title = 'Unable to link toll account';
            description = <>
                <p>The toll account details you entered don't match the details provided by the toll provider.</p>
                <p>Check if the information you entered is correct and matches your toll provider's records. If you have recently updated your details with your toll provider, please wait at least 3 days before trying again.</p>
                <p>If you need help, call {PhoneElement}.</p>
            </>;
            break;
        case TollAccountFailure.customerHasTollAccountLinkedToAnotherMyAccountId:
        case TollAccountFailure.tollAccountOutsideNSW:
        case TollAccountFailure.tollAccountCannotBeVerified:
            title = 'Unable to link toll account';
            description = <>
                <p>The toll account details you have provided don't match the details provided by the toll provider.</p>
                <p>Check the details from your toll provider are correct and try again. If the problem continues, contact your toll provider.</p>
            </>;
            break;
        case TollAccountFailure.tspApiTollAccountNotFound:
            title = 'Unable to link toll account';
            description = <>
                <p>The toll account details you entered don't match the details provided by the toll provider.</p>
                <p>Check if the information you entered is correct and matches your toll provider's records. If you have recently updated
                    your details with your toll provider, please wait at least 3 days before trying again.
                </p>
                <p>If the problem continues, contact your toll provider.</p>
            </>;
            break;
        case TollAccountFailure.tollAccountProductTypeInvalid:
            title = 'We cannot link this toll account';
            description = <>
                <p>The toll account provided is associated with an invalid toll product. </p>
                <p>Casual toll products (such as LinktGO) or special passes (for example, the ERider and eMU passes) are not eligible for Toll Relief.
                    Check the {' '}
                    <TextLink href={config.links.eligibilityCriteria} target='_blank' rel='noreferrer noopener'
                        >eligibility criteria
                    </TextLink>.
                </p>
                <p>If you need help, call {PhoneElement}.</p>
            </>;
            break;
        case TollAccountFailure.tollAccountNotAssociatedToABN:
            title = 'Unable to link this toll account';
            description = <>
                <p>Your toll account is not associated with an ABN. Personal toll accounts can only be linked as 'Personal' or
                    'Business and personal (mixed personal toll account)'.</p>
                <p>To link a 'Business and personal (mixed personal toll account)' you'll need to unlink all other toll accounts.
                    You can only link one personal account.</p>
                <p><TextLink href={routes.manageTollAccount.path}>Manage toll accounts</TextLink></p>
            </>;
            break;
        case TollAccountFailure.tollAccountAlreadyLinked:
            title = 'Unable to link this toll account';
            description = <>
                <p>This toll account is already linked to another MyServiceNSW Account. Only one person can claim toll relief on this account at a time.</p>
                <p>If you need any assistance, contact us on {PhoneElement}.</p>
            </>;
            break;
        case TollAccountFailure.serviceProviderError:
            title = 'Unable to process your request';
            description = <>
                <p>We could not process your request due to a technical issue on our end.
                </p>
                <p>
                    Please try again. If the issue keeps happening, call us on {PhoneElement}.
                </p>
            </>;
            break;
        case TollAccountFailure.etollApiNotAvailable:
        case TollAccountFailure.linktApiNotAvailable:
            title = 'Service interruption';
            description =
                <p>It looks like there may be an interruption to the service, which may affect the processing of your request.
                    We recommend returning here at later time and trying again.
                </p>;
            break;
        default:
            console.error(`Toll account ${errorReason} failure not recognized`);
            title = 'Unable to link this toll account';
            description = <></>;
    }

    return [title, description];
}

export default ApiErrorAlert;
